import { useCallback, useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildWsUrl(token) {
  const httpUrl = `${BACKEND_URL}/api/ws`;
  return httpUrl.replace(/^http/i, 'ws') + `?token=${encodeURIComponent(token)}`;
}

/**
 * Real-time hook: subscribes to a date room and notifies callbacks when
 * order updates or daily_meta changes arrive. Tracks presence list.
 */
export function useRealtime({ date, onOrderUpdate, onDailyMeta }) {
  const wsRef = useRef(null);
  const clientIdRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState([]);
  const onOrderRef = useRef(onOrderUpdate);
  const onMetaRef = useRef(onDailyMeta);
  onOrderRef.current = onOrderUpdate;
  onMetaRef.current = onDailyMeta;

  // Open connection once
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    let cancelled = false;
    let pingTimer = null;
    let reconnectTimer = null;

    const open = () => {
      try {
        const ws = new WebSocket(buildWsUrl(token));
        wsRef.current = ws;
        ws.onopen = () => {
          if (cancelled) return;
          setConnected(true);
        };
        ws.onmessage = (ev) => {
          let msg;
          try {
            msg = JSON.parse(ev.data);
          } catch {
            return;
          }
          if (msg.type === 'hello') {
            clientIdRef.current = msg.client_id;
          } else if (msg.type === 'presence') {
            setPresence(msg.users || []);
          } else if (msg.type === 'order_update') {
            if (onOrderRef.current) onOrderRef.current(msg);
          } else if (msg.type === 'daily_meta') {
            if (onMetaRef.current) onMetaRef.current(msg);
          }
        };
        ws.onclose = () => {
          setConnected(false);
          if (!cancelled) {
            reconnectTimer = setTimeout(open, 3000);
          }
        };
        ws.onerror = () => {
          try { ws.close(); } catch {}
        };
      } catch {
        reconnectTimer = setTimeout(open, 3000);
      }
    };

    open();
    pingTimer = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
    }, 25000);

    return () => {
      cancelled = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      const ws = wsRef.current;
      if (ws) try { ws.close(); } catch {}
      wsRef.current = null;
    };
  }, []);

  // Subscribe to date on change
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !date) return;
    ws.send(JSON.stringify({ type: 'subscribe', date }));
    return () => {
      // unsubscribed implicitly when re-subscribing
    };
  }, [date, connected]);

  const setEditing = useCallback((customerId) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'editing', customer_id: customerId }));
  }, []);

  return { connected, presence, setEditing, clientId: clientIdRef.current };
}
